/**
 * useFotografias - Hook para gerenciar estado e operações da aba Fotografias
 *
 * MOCKUP GLOBAL: Dados estáticos para demonstração visual
 * Mesmos dados aparecem em todas as contagens
 */
import { useState, useMemo, useCallback } from 'react';

// ==================== TIPOS ====================

export interface MockFoto {
  id: number;
  fileName: string;
  type: 'image' | 'pdf';
  classificacao: string;
  classificacaoAbrev: string;
  cliente: string;
  clienteId: number;
  descricao?: string;
  dataUpload: string;
  tamanho: string;
  // Epic 2 - Story 2.4: Campos GPS para evidências reais
  hasGps?: boolean;
  gpsDisplay?: string;
  arquivoUrl?: string | null;
}

export interface MockCliente {
  id: number;
  nome: string;
  fotos: number;
}

export interface ClassificacaoDocumento {
  id: number;
  classificacao: string;
  abreviacao: string;
}

export interface FilterOption {
  value: string | number;
  label: string;
}

// ==================== DADOS MOCKADOS GLOBAIS ====================

const MOCK_CLASSIFICACOES: ClassificacaoDocumento[] = [
  { id: 1, classificacao: 'Evidência Sobra', abreviacao: 'Ev_Sobra' },
  { id: 2, classificacao: 'Evidência Segregação', abreviacao: 'Ev_Segreg' },
  { id: 3, classificacao: 'Evidência Lacre', abreviacao: 'Ev_Lacre' },
  { id: 4, classificacao: 'Evidência Checklist', abreviacao: 'Ev_Check' },
  { id: 5, classificacao: 'Evidência Gasto Cirúrgico', abreviacao: 'Ev_Cirurgico' },
  { id: 6, classificacao: 'Evidência Produto', abreviacao: 'Ev_Prod' },
  { id: 7, classificacao: 'Evidência Avaria', abreviacao: 'Ev_Avaria' },
  { id: 8, classificacao: 'Relatório Sobra', abreviacao: 'Rel_Sobra' },
  { id: 9, classificacao: 'Relatório Falta', abreviacao: 'Rel_Falta' },
  { id: 10, classificacao: 'Relatório Diferença', abreviacao: 'Rel_Dif' },
];

const MOCK_CLIENTES: MockCliente[] = [
  { id: 1, nome: 'Johnson & Johnson', fotos: 4 },
  { id: 2, nome: '3M do Brasil', fotos: 3 },
  { id: 3, nome: 'Medtronic', fotos: 2 },
  { id: 4, nome: 'Boston Scientific', fotos: 2 },
];

const MOCK_FOTOS: MockFoto[] = [
  {
    id: 1,
    fileName: 'lacre_01.jpg',
    type: 'image',
    classificacao: 'Evidência Lacre',
    classificacaoAbrev: 'Ev_Lacre',
    cliente: 'Johnson & Johnson',
    clienteId: 1,
    descricao: 'Lacre do lote XYZ-123 verificado',
    dataUpload: '2026-01-15T10:30:00',
    tamanho: '2.4 MB'
  },
  {
    id: 2,
    fileName: 'sobra_01.jpg',
    type: 'image',
    classificacao: 'Evidência Sobra',
    classificacaoAbrev: 'Ev_Sobra',
    cliente: 'Johnson & Johnson',
    clienteId: 1,
    descricao: 'Produto excedente encontrado na prateleira 3',
    dataUpload: '2026-01-15T10:35:00',
    tamanho: '1.8 MB'
  },
  {
    id: 3,
    fileName: 'cx_danificada.jpg',
    type: 'image',
    classificacao: 'Evidência Avaria',
    classificacaoAbrev: 'Ev_Avaria',
    cliente: 'Johnson & Johnson',
    clienteId: 1,
    descricao: 'Caixa com avaria no transporte',
    dataUpload: '2026-01-15T11:00:00',
    tamanho: '3.1 MB'
  },
  {
    id: 4,
    fileName: 'etiqueta_lote.jpg',
    type: 'image',
    classificacao: 'Evidência Produto',
    classificacaoAbrev: 'Ev_Prod',
    cliente: '3M do Brasil',
    clienteId: 2,
    descricao: 'Etiqueta do lote ABC-456',
    dataUpload: '2026-01-15T11:15:00',
    tamanho: '1.2 MB'
  },
  {
    id: 5,
    fileName: 'serial_number.jpg',
    type: 'image',
    classificacao: 'Evidência Produto',
    classificacaoAbrev: 'Ev_Prod',
    cliente: '3M do Brasil',
    clienteId: 2,
    descricao: 'Número de série do equipamento',
    dataUpload: '2026-01-15T11:20:00',
    tamanho: '0.9 MB'
  },
  {
    id: 6,
    fileName: 'transporte.pdf',
    type: 'pdf',
    classificacao: 'Relatório Diferença',
    classificacaoAbrev: 'Rel_Dif',
    cliente: 'Medtronic',
    clienteId: 3,
    descricao: 'Documento de transporte',
    dataUpload: '2026-01-15T14:00:00',
    tamanho: '458 KB'
  },
  {
    id: 7,
    fileName: 'nota_fiscal.pdf',
    type: 'pdf',
    classificacao: 'Relatório Sobra',
    classificacaoAbrev: 'Rel_Sobra',
    cliente: 'Medtronic',
    clienteId: 3,
    descricao: 'Nota fiscal de referência',
    dataUpload: '2026-01-15T14:30:00',
    tamanho: '312 KB'
  },
  {
    id: 8,
    fileName: 'foto_contexto.jpg',
    type: 'image',
    classificacao: 'Evidência Checklist',
    classificacaoAbrev: 'Ev_Check',
    cliente: 'Boston Scientific',
    clienteId: 4,
    descricao: 'Foto geral do local de armazenamento',
    dataUpload: '2026-01-15T15:00:00',
    tamanho: '4.2 MB'
  },
  {
    id: 9,
    fileName: 'item_avulso.jpg',
    type: 'image',
    classificacao: 'Evidência Segregação',
    classificacaoAbrev: 'Ev_Segreg',
    cliente: 'Boston Scientific',
    clienteId: 4,
    descricao: 'Item segregado para análise',
    dataUpload: '2026-01-15T15:30:00',
    tamanho: '2.1 MB'
  },
  {
    id: 10,
    fileName: 'rack_02.jpg',
    type: 'image',
    classificacao: 'Evidência Lacre',
    classificacaoAbrev: 'Ev_Lacre',
    cliente: 'Johnson & Johnson',
    clienteId: 1,
    descricao: 'Lacre do rack 02',
    dataUpload: '2026-01-15T16:00:00',
    tamanho: '1.5 MB'
  },
  {
    id: 11,
    fileName: 'panoramica.jpg',
    type: 'image',
    classificacao: 'Evidência Checklist',
    classificacaoAbrev: 'Ev_Check',
    cliente: '3M do Brasil',
    clienteId: 2,
    descricao: 'Vista panorâmica do estoque',
    dataUpload: '2026-01-15T16:30:00',
    tamanho: '5.8 MB'
  },
];

// ==================== OPÇÕES DE FILTRO ====================

export const TIPO_GERAL_OPTIONS: FilterOption[] = [
  { value: 'evidencia', label: 'Evidência' },
  { value: 'relatorio', label: 'Relatório' },
];

export const TIPO_ARQUIVO_OPTIONS: FilterOption[] = [
  { value: 'image', label: 'Imagem' },
  { value: 'pdf', label: 'PDF' },
];

// ==================== HOOK ====================

/**
 * Hook para gerenciar fotografias
 * @param globalClienteFilter - Filtro global de cliente vindo do header (opcional)
 *                              Se fornecido, usa este filtro ao invés do local
 */
export function useFotografias(globalClienteFilter?: (string | number)[]) {
  // Estado base
  const [fotos, setFotos] = useState<MockFoto[]>(MOCK_FOTOS);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Estados de filtro (cliente usa filtro global quando disponível)
  const [selectedTipoGeral, setSelectedTipoGeral] = useState<(string | number)[]>([]);
  const [selectedClassificacaoIds, setSelectedClassificacaoIds] = useState<(string | number)[]>([]);
  const [selectedTipoArquivo, setSelectedTipoArquivo] = useState<(string | number)[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Usa filtro global se fornecido, senão array vazio (sem filtro de cliente)
  const effectiveClienteFilter = globalClienteFilter ?? [];

  // Dados estáticos
  const clientes = MOCK_CLIENTES;
  const classificacoes = MOCK_CLASSIFICACOES;

  // Opções de filtro formatadas
  const clienteOptions: FilterOption[] = useMemo(() =>
    clientes.map(c => ({ value: c.id, label: c.nome })),
    [clientes]
  );

  const classificacaoOptions: FilterOption[] = useMemo(() =>
    classificacoes.map(c => ({ value: c.id, label: c.classificacao })),
    [classificacoes]
  );

  // Fotos filtradas (combinando TODOS os filtros)
  const filteredFotos = useMemo(() => {
    let result = fotos;

    // Filtrar por cliente (usa filtro global quando disponível)
    if (effectiveClienteFilter.length > 0) {
      result = result.filter(f => effectiveClienteFilter.includes(f.clienteId));
    }

    // Filtrar por Tipo Geral (Evidência / Relatório)
    if (selectedTipoGeral.length > 0) {
      result = result.filter(f => {
        const isEvidencia = f.classificacaoAbrev.startsWith('Ev_');
        const isRelatorio = f.classificacaoAbrev.startsWith('Rel_');
        return (
          (selectedTipoGeral.includes('evidencia') && isEvidencia) ||
          (selectedTipoGeral.includes('relatorio') && isRelatorio)
        );
      });
    }

    // Filtrar por classificação específica (multi-select)
    if (selectedClassificacaoIds.length > 0) {
      const selectedAbrevs = classificacoes
        .filter(c => selectedClassificacaoIds.includes(c.id))
        .map(c => c.abreviacao);
      result = result.filter(f => selectedAbrevs.includes(f.classificacaoAbrev));
    }

    // Filtrar por tipo de arquivo (Imagem / PDF)
    if (selectedTipoArquivo.length > 0) {
      result = result.filter(f => selectedTipoArquivo.includes(f.type));
    }

    // Filtrar por busca (nome do arquivo)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(f =>
        f.fileName.toLowerCase().includes(term) ||
        f.descricao?.toLowerCase().includes(term) ||
        f.classificacao.toLowerCase().includes(term)
      );
    }

    return result;
  }, [fotos, effectiveClienteFilter, selectedTipoGeral, selectedClassificacaoIds, selectedTipoArquivo, searchTerm, classificacoes]);

  // Contadores
  const counts = useMemo(() => {
    const images = filteredFotos.filter(f => f.type === 'image').length;
    const pdfs = filteredFotos.filter(f => f.type === 'pdf').length;
    return { images, pdfs, total: images + pdfs };
  }, [filteredFotos]);

  // Verificar se há filtros ativos (não inclui cliente pois é filtro global)
  const hasActiveFilters = useMemo(() => {
    return (
      selectedTipoGeral.length > 0 ||
      selectedClassificacaoIds.length > 0 ||
      selectedTipoArquivo.length > 0 ||
      searchTerm.trim() !== ''
    );
  }, [selectedTipoGeral, selectedClassificacaoIds, selectedTipoArquivo, searchTerm]);

  // Limpar todos os filtros locais (não limpa filtro global de cliente)
  const clearAllFilters = useCallback(() => {
    setSelectedTipoGeral([]);
    setSelectedClassificacaoIds([]);
    setSelectedTipoArquivo([]);
    setSearchTerm('');
  }, []);

  // Lightbox handlers
  const openLightbox = useCallback((index: number) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentPhotoIndex(prev =>
      prev > 0 ? prev - 1 : filteredFotos.length - 1
    );
  }, [filteredFotos.length]);

  const goToNext = useCallback(() => {
    setCurrentPhotoIndex(prev =>
      prev < filteredFotos.length - 1 ? prev + 1 : 0
    );
  }, [filteredFotos.length]);

  // CRUD mockado
  const updateFoto = useCallback((id: number, updates: Partial<MockFoto>) => {
    setFotos(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  }, []);

  const deleteFoto = useCallback((id: number) => {
    setFotos(prev => prev.filter(f => f.id !== id));
  }, []);

  const addFoto = useCallback((newFoto: Omit<MockFoto, 'id'>) => {
    const maxId = Math.max(...fotos.map(f => f.id), 0);
    setFotos(prev => [...prev, { ...newFoto, id: maxId + 1 }]);
  }, [fotos]);

  return {
    // Estado
    fotos: filteredFotos,
    allFotos: fotos,
    clientes,
    classificacoes,
    lightboxOpen,
    currentPhotoIndex,

    // Opções de filtro (clienteOptions mantido para referência, mas não usado nos filtros locais)
    clienteOptions,
    classificacaoOptions,
    tipoGeralOptions: TIPO_GERAL_OPTIONS,
    tipoArquivoOptions: TIPO_ARQUIVO_OPTIONS,

    // Estados de filtro (selectedClienteIds removido - usa filtro global)
    selectedTipoGeral,
    selectedClassificacaoIds,
    selectedTipoArquivo,
    searchTerm,

    // Contadores
    counts,
    hasActiveFilters,

    // Setters de filtro (setSelectedClienteIds removido - usa filtro global)
    setSelectedTipoGeral,
    setSelectedClassificacaoIds,
    setSelectedTipoArquivo,
    setSearchTerm,
    clearAllFilters,

    // Lightbox
    openLightbox,
    closeLightbox,
    goToPrevious,
    goToNext,
    setCurrentPhotoIndex,

    // CRUD
    updateFoto,
    deleteFoto,
    addFoto,
  };
}

export default useFotografias;
